/** Cardless merchant id; same in staging + production */
const CARDLESS_MID = '4045998259';
var TransactionsEndpoints;
(function (TransactionsEndpoints) {
    TransactionsEndpoints["CreateDispute"] = "/transactions/create-dispute";
    TransactionsEndpoints["GetNotificationSettings"] = "/transactions/get-notification-settings";
    TransactionsEndpoints["GetTransactions"] = "/transactions/get-transactions";
    TransactionsEndpoints["UpdateNotificationSettings"] = "/transactions/update-notification-settings";
    TransactionsEndpoints["GetTransactionsExportedURL"] = "/transactions/exported-url";
    TransactionsEndpoints["ExportTransactions"] = "/transactions/export";
    TransactionsEndpoints["GetTransactionById"] = "/transactions/get-transaction-by-id";
    TransactionsEndpoints["GetTransactionByIdV2"] = "/transactions/v2/get-transaction-by-id";
    /** List every dispute on an account */
    TransactionsEndpoints["ListDisputes"] = "/transactions/list-disputes";
})(TransactionsEndpoints || (TransactionsEndpoints = {}));
/**
 * Statuses in the transaction status lifecycle.
 * @see https://docs.google.com/document/d/1cHhtzvJxz4vb28q0CgEqor8nn9y0XOjYyWmI9H-RgOI/edit?usp=sharing for more info.
 */
var TransactionStatus;
(function (TransactionStatus) {
    TransactionStatus["Unspecified"] = "TRANSACTION_STATUS_UNSPECIFIED";
    /** Transaction is pending. Current balance has not been modified yet */
    TransactionStatus["Pending"] = "PENDING";
    /**
     * Transaction was settled and the account's current balance has been modified with the processor.
     * This is a valid end state of the transaction lifecycle.
     */
    TransactionStatus["Settled"] = "SETTLED";
    /**
     * Auth was declined.
     * This transaction doesn't affect the account's current balance or credit limit, because it effectively never happened.
     * Useful for showing the user when their transaction was declined.
     */
    TransactionStatus["Declined"] = "DECLINED";
    /**
     * Auth expired.
     * This transaction doesn't affect the account's current balance and their credit limit is free up.
     * Auths can expire for a variety of reasons.
     *   e.g. I buy a burrito at Chipotle (successful auth), eat it, then the next day the auth expires due to a merchant issue.
     *     I just got a free burrito which I don't have to then pay for.
     */
    TransactionStatus["AuthExpired"] = "AUTH_EXPIRED";
})(TransactionStatus || (TransactionStatus = {}));
/**
 * These are the different types of transactions that are stored in our system.
 * The transaction type informs what TransactionStatuses are valid for a given transaction.
 */
var TransactionType;
(function (TransactionType) {
    TransactionType["Unspecified"] = "TRANSACTION_TYPE_UNSPECIFIED";
    /**
     * A normal purchase from a merchant, e.g. buy a coffee.
     *
     * A PURCHASE starts with an authorization, which can be either approved or declined.
     *
     * If the auth is approved, the transaction will be in the PENDING state, until it settles,
     * in which case it will end in the SETTLED state.
     *
     * The available credit of the account will decrease when this transaction is authorized.
     * The available credit of the account will possibly change when this transaction is settled,
     * if the authorized amount is different from the settled amount.
     * The current balance of the account will increase once this transaction is settled.
     *
     * If declined, the transaction will start and remain in the DECLINED state.
     * It will not affect the balance or available credit.
     */
    TransactionType["Purchase"] = "PURCHASE";
    /**
     * A payment to the account, e.g. ACH to pay off current balance.
     *
     * A PAYMENT transaction will be immediately initiated, but the funds are "on hold" until the payment goes through.
     * During this "hold" period (usually ~3 days), the transaction will be PENDING, and once the "hold" is lifted
     * it will be SETTLED. During the "hold" period, a payment can be DECLINED.
     *
     * The available credit of the account will increase when this transaction is settled.
     * The current balance of the account will decrease when this transaction is initiated.
     */
    TransactionType["Payment"] = "PAYMENT";
    /**
     * A transaction is refunded by the merchant, e.g. return a shirt and get refunded by Nordstrom.
     *
     * A REFUND transaction has the same status lifecycle as a PURCHASE.
     *
     * The available credit of the account will increase when this transaction is settled.
     * The current balance of the account will decrease once this transaction settles.
     *
     * A REFUND transaction will be a different transaction object than the original PURCHASE transaction.
     */
    TransactionType["Refund"] = "REFUND";
    /**
     * A transaction chargeback is processed.
     *
     * A CHARGEBACK transaction will be immediately authorized, but it still takes time
     * to reach the settled state. TODO verify with CoreCard if this is true
     *
     * The available credit of the account will increase when this transaction is settled.
     * The current balance of the account will decrease once this transaction settles.
     *
     * A CHARGEBACK transaction will be a different transaction object than the original PURCHASE transaction.
     */
    TransactionType["Chargeback"] = "CHARGEBACK";
    /**
     * A fee for the account, e.g. annual fee for a premium card.
     *
     * A FEE transaction will start in the SETTLED state.
     *
     * The available credit of the account will decrease.
     * The current balance of the account will increase.
     */
    TransactionType["Fee"] = "FEE";
    /**
     * A credit to the statement balance, e.g. a rewards redemption.
     *
     * A STATEMENT_CREDIT transaction will start in the SETTLED state.
     *
     * The available credit of the account will decrease.
     * The current balance of the account will decrease.
     */
    TransactionType["StatementCredit"] = "STATEMENT_CREDIT";
    /**
     * Interest charge was added to the account, e.g. interest accrued from an account carrying a balance.
     *
     * An INTEREST transaction will start in the SETTLED state.
     *
     * The available credit will decrease. (TODO verify w/ CC if this is true)
     * The current balance of the account will increase.
     */
    TransactionType["Interest"] = "INTEREST";
    /**
     * Balance adjustment to correct an error.
     *
     * AN ADJUSTMENT transaction will start in the SETTLED state.
     *
     * There is no guarantee on how this type of transaction affects available credit and current balance.
     */
    TransactionType["Adjustment"] = "ADJUSTMENT";
    /**
     * Reversal of previous transaction.
     *
     * A REVERSAL transaction will start in the SETTLED state.
     *
     * There is no guarantee on how this type of transaction affects available credit and current balance.
     */
    TransactionType["Reversal"] = "REVERSAL";
    /**
     * Dispute of a purchase transaction.
     *
     * A DISPUTE transaction will start in the SETTLED state.
     *
     * The available credit will increase.
     * The current balance of the account will decrease.
     * @see https://coretrack.corecard.com/edit_bug.aspx?id=102561
     */
    TransactionType["Dispute"] = "DISPUTE";
    /**
     * Merchant offers from American Express that will appear as debits, but we do not want to deduct points for them.
     *
     * An OFFER transaction will start in the SETTLED state.
     *
     * The available credit will increase.
     * The current balance of the account will decrease.
     * @see https://coretrack.corecard.com/edit_bug.aspx?id=110002
     */
    TransactionType["Offer"] = "OFFER";
    /**
     * Cash advance from an ATM.
     * A CASH_ADVANCE transaction will start in the PENDING state.
     *
     * The available credit of the account will decrease when this transaction is authorized.
     */
    TransactionType["CashAdvance"] = "CASH_ADVANCE";
    /**
     * A balance transfer from another credit card to this account.
     * A BALANCE_TRANSFER transaction will start in the SETTLED state.
     *
     * The current balance of the account will increase by the transfer amount.
     */
    TransactionType["BalanceTransfer"] = "BALANCE_TRANSFER";
    /** Money received into a debit (checking) account. */
    TransactionType["Deposit"] = "DEPOSIT";
    /** Money sent out of a debit (checking) account. */
    TransactionType["Withdrawal"] = "WITHDRAWAL";
})(TransactionType || (TransactionType = {}));
var TransactionSubType;
(function (TransactionSubType) {
    TransactionSubType["Unspecified"] = "TRANSACTION_SUB_TYPE_UNSPECIFIED";
    /**
     * Purchase types
     */
    /** @deprecated Use {@link SubscriptionFee} */
    TransactionSubType["CoinbaseOneSubscription"] = "COINBASE_ONE_SUBSCRIPTION";
    TransactionSubType["SubscriptionFee"] = "SUBSCRIPTION_FEE";
    TransactionSubType["RentPayment"] = "RENT_PAYMENT";
    /**
     * Fee types
     */
    TransactionSubType["AnnualFee"] = "ANNUAL_FEE";
    TransactionSubType["LateFee"] = "LATE_FEE";
    TransactionSubType["ReturnedPaymentFee"] = "RETURNED_PAYMENT_FEE";
    TransactionSubType["MinimumInterestFee"] = "MINIMUM_INTEREST_FEE";
    TransactionSubType["BalanceTransferFee"] = "BALANCE_TRANSFER_FEE";
    TransactionSubType["BalanceTransferFeeReversal"] = "BALANCE_TRANSFER_FEE_REVERSAL";
    /**
     * Adjustment types
     */
    TransactionSubType["InterestAdjustment"] = "INTEREST_ADJUSTMENT";
    TransactionSubType["FeeAdjustment"] = "FEE_ADJUSTMENT";
    TransactionSubType["RewardsAdjustment"] = "REWARDS_ADJUSTMENT";
    TransactionSubType["CashAdvanceFee"] = "CASH_ADVANCE_FEE";
    TransactionSubType["CardReplacementFee"] = "CARD_REPLACEMENT_FEE";
    TransactionSubType["CashAdvanceFeeReversal"] = "CASH_ADVANCE_FEE_REVERSAL";
    TransactionSubType["BiltRentPaymentAdjustment"] = "BILT_RENT_PAYMENT_ADJUSTMENT";
    /**
     * Dispute types
     */
    TransactionSubType["DisputeCredit"] = "DISPUTE_CREDIT";
    TransactionSubType["DisputeDebit"] = "DISPUTE_DEBIT";
    /**
     * Reversal types
     */
    TransactionSubType["PaymentReversal"] = "PAYMENT_REVERSAL";
    TransactionSubType["PurchaseReversal"] = "PURCHASE_REVERSAL";
    TransactionSubType["CashAdvanceReversal"] = "CASH_ADVANCE_REVERSAL";
    /** @deprecated Use {@link SubscriptionFeeReversal} */
    TransactionSubType["CoinbaseOneSubscriptionReversal"] = "COINBASE_ONE_SUBSCRIPTION_REVERSAL";
    TransactionSubType["SubscriptionFeeReversal"] = "SUBSCRIPTION_FEE_REVERSAL";
    /** CoreCard tran code 673407 — a Kraken-only credit that waives a subscription fee, kept distinct from a reversal */
    TransactionSubType["SubscriptionFeeWaiver"] = "SUBSCRIPTION_FEE_WAIVER";
    TransactionSubType["RewardsAdjustmentReversal"] = "REWARDS_ADJUSTMENT_REVERSAL";
    TransactionSubType["PurchaseAdjustment"] = "PURCHASE_ADJUSTMENT";
    TransactionSubType["BiltRentPaymentAdjustmentReversal"] = "BILT_RENT_PAYMENT_ADJUSTMENT_REVERSAL";
    TransactionSubType["BiltRentPaymentReversal"] = "BILT_RENT_PAYMENT_REVERSAL";
    /**
     * Payment types
     */
    TransactionSubType["AchPayment"] = "ACH_PAYMENT";
    /** Coinbase-specific; other partners use {@link CryptoPayment} */
    TransactionSubType["CoinbaseCryptoPayment"] = "COINBASE_CRYPTO_PAYMENT";
    TransactionSubType["CryptoPayment"] = "CRYPTO_PAYMENT";
    TransactionSubType["BillPayment"] = "BILL_PAYMENT";
    TransactionSubType["CheckPayment"] = "CHECK_PAYMENT";
    /**
     * Deposit types (debit accounts)
     */
    TransactionSubType["DirectDeposit"] = "DIRECT_DEPOSIT";
    TransactionSubType["AchTransferIn"] = "ACH_TRANSFER_IN";
    TransactionSubType["DepositReturn"] = "DEPOSIT_RETURN";
    /**
     * Withdrawal types (debit accounts)
     */
    TransactionSubType["AtmWithdrawal"] = "ATM_WITHDRAWAL";
    TransactionSubType["AchTransferOut"] = "ACH_TRANSFER_OUT";
})(TransactionSubType || (TransactionSubType = {}));
const FEE_SUB_TYPES = new Set([
    TransactionSubType.AnnualFee,
    TransactionSubType.LateFee,
    TransactionSubType.ReturnedPaymentFee,
    TransactionSubType.MinimumInterestFee,
    TransactionSubType.BalanceTransferFee,
    TransactionSubType.BalanceTransferFeeReversal,
]);
/**
 * The merchant category that a PURCHASE or REFUND transaction falls under.
 * MerchantCategory is defined by the MCC code.
 * @see https://www.notion.so/MCC-Codes-69dde4f01fed4f188736e04aec28c503
 */
var MerchantCategory;
(function (MerchantCategory) {
    MerchantCategory["Unspecified"] = "MERCHANT_CATEGORY_UNSPECIFIED";
    /**
     * The transaction's mcc code doesn't fall under any of our defined categories.
     */
    MerchantCategory["Other"] = "OTHER";
    /**
     * Spending within the brand associated with this account This will override the
     * specific MCC code that a transaction falls under -- e.g. if we had an Airbnb
     * card, an Airbnb purchase would be categorized as BRAND spend under an Airbnb
     * account, not as TRAVEL.
     */
    MerchantCategory["Brand"] = "BRAND";
    MerchantCategory["Advertising"] = "ADVERTISING";
    MerchantCategory["Airline"] = "AIRLINES";
    MerchantCategory["Automotive"] = "AUTOMOTIVE";
    MerchantCategory["BusinessServices"] = "BUSINESS_SERVICES";
    MerchantCategory["CarRental"] = "CAR_RENTAL";
    MerchantCategory["Clothing"] = "CLOTHING";
    MerchantCategory["ContractedServices"] = "CONTRACTED_SERVICES";
    MerchantCategory["Dining"] = "DINING";
    MerchantCategory["DigitalGoods"] = "DIGITAL_GOODS";
    MerchantCategory["DiscountStores"] = "DISCOUNT_STORES";
    MerchantCategory["Donation"] = "DONATION";
    MerchantCategory["Electronics"] = "ELECTRONICS";
    MerchantCategory["Entertainment"] = "ENTERTAINMENT";
    MerchantCategory["EvCharging"] = "EV_CHARGING";
    MerchantCategory["FinancialServices"] = "FINANCIAL_SERVICES";
    MerchantCategory["Fitness"] = "FITNESS";
    MerchantCategory["FoodDelivery"] = "FOOD_DELIVERY";
    MerchantCategory["Gas"] = "GAS";
    MerchantCategory["GiftCard"] = "GIFT_CARD";
    MerchantCategory["GovernmentServices"] = "GOVERNMENT_SERVICES";
    MerchantCategory["Groceries"] = "GROCERIES";
    MerchantCategory["Hardware"] = "HARDWARE";
    MerchantCategory["Healthcare"] = "HEALTHCARE";
    MerchantCategory["Hotel"] = "HOTEL";
    MerchantCategory["HomeGoods"] = "HOME_GOODS";
    MerchantCategory["Insurance"] = "INSURANCE";
    MerchantCategory["LiquorStores"] = "LIQUOR_STORES";
    MerchantCategory["Lodging"] = "LODGING";
    MerchantCategory["OtherTravel"] = "OTHER_TRAVEL";
    MerchantCategory["Parking"] = "PARKING";
    MerchantCategory["PersonalServices"] = "PERSONAL_SERVICES";
    MerchantCategory["Pharmacy"] = "PHARMACY";
    MerchantCategory["ProfessionalServices"] = "PROFESSIONAL_SERVICES";
    MerchantCategory["RealEstate"] = "REAL_ESTATE";
    MerchantCategory["RepairServices"] = "REPAIR_SERVICES";
    MerchantCategory["Retail"] = "RETAIL";
    MerchantCategory["Rideshare"] = "RIDESHARE";
    MerchantCategory["Shipping"] = "SHIPPING";
    MerchantCategory["Shopping"] = "SHOPPING";
    MerchantCategory["Streaming"] = "STREAMING";
    MerchantCategory["SubscriptionServices"] = "SUBSCRIPTION_SERVICES";
    MerchantCategory["Transit"] = "TRANSIT";
    MerchantCategory["Travel"] = "TRAVEL";
    MerchantCategory["TravelAgencies"] = "TRAVEL_AGENCIES";
    MerchantCategory["Utilities"] = "UTILITIES";
    MerchantCategory["Wholesale"] = "WHOLESALE";
})(MerchantCategory || (MerchantCategory = {}));
/**
 * Type of digital wallet used to initiate a transaction, instead of a physical/virtual card.
 */
var DigitalWalletType;
(function (DigitalWalletType) {
    DigitalWalletType["Unspecified"] = "DIGITAL_WALLET_TYPE_UNSPECIFIED";
    DigitalWalletType["ApplePay"] = "APPLE_PAY";
    DigitalWalletType["GooglePay"] = "GOOGLE_PAY";
})(DigitalWalletType || (DigitalWalletType = {}));
/**
 * A derived type that consolidates the four types of payment methods for a transaction.
 * This enum doesn't exist in the backend, but is a convenience enum for use in the frontend.
 */
var PaymentMethod;
(function (PaymentMethod) {
    PaymentMethod["Physical"] = "PHYSICAL";
    PaymentMethod["Virtual"] = "VIRTUAL";
    PaymentMethod["ApplePay"] = "APPLE_PAY";
    PaymentMethod["GooglePay"] = "GOOGLE_PAY";
})(PaymentMethod || (PaymentMethod = {}));
var TransactionRewardsType;
(function (TransactionRewardsType) {
    TransactionRewardsType["REWARDS_TYPE_UNSPECIFIED"] = "REWARDS_TYPE_UNSPECIFIED";
    TransactionRewardsType["POINTS"] = "POINTS";
    TransactionRewardsType["PAYMENT_PLAN"] = "PAYMENT_PLAN";
})(TransactionRewardsType || (TransactionRewardsType = {}));
/**
 * Used to specify the reason why an auth transaction is declined.
 */
var DeclineReason;
(function (DeclineReason) {
    DeclineReason["AccountStatusIsAutoClose"] = "ACCOUNT_STATUS_IS_AUTO_CLOSE";
    DeclineReason["AccountStatusIsBankrupt"] = "ACCOUNT_STATUS_IS_BANKRUPT";
    DeclineReason["AccountStatusIsBlocked"] = "ACCOUNT_STATUS_IS_BLOCKED";
    DeclineReason["AccountStatusIsChargeOff"] = "ACCOUNT_STATUS_IS_CHARGE_OFF";
    DeclineReason["AccountStatusIsClosed"] = "ACCOUNT_STATUS_IS_CLOSED";
    DeclineReason["AccountStatusIsClosedByCustomer"] = "ACCOUNT_STATUS_IS_CLOSED_BY_CUSTOMER";
    DeclineReason["AccountStatusIsClosedMerchant"] = "ACCOUNT_STATUS_IS_CLOSED_MERCHANT";
    DeclineReason["AccountStatusIsDelinquent"] = "ACCOUNT_STATUS_IS_DELINQUENT";
    /** @deprecated */
    DeclineReason["AccountStatusIsFraud"] = "ACCOUNT_STATUS_IS_FRAUD";
    DeclineReason["AccountStatusIsFrozen"] = "ACCOUNT_STATUS_IS_FROZEN";
    /** @deprecated */
    DeclineReason["AccountStatusIsLostCard"] = "ACCOUNT_STATUS_IS_LOST_CARD";
    /** @deprecated */
    DeclineReason["AccountStatusIsStolen"] = "ACCOUNT_STATUS_IS_STOLEN";
    DeclineReason["ArqcVerificationFailed"] = "ARQC_VERIFICATION_FAILED";
    DeclineReason["AtcCheckFailed"] = "ATC_CHECK_FAILED";
    DeclineReason["AvsAuthDeclined"] = "AVS_AUTH_DECLINED";
    /** @deprecated */
    DeclineReason["AvsOnlyNoMatch"] = "AVS_ONLY_NO_MATCH";
    DeclineReason["AuthorizedUserLimitExceeded"] = "AUTHORIZED_USER_LIMIT_EXCEEDED";
    DeclineReason["CardStatusIsAdditionalCardPendingActivation"] = "CARD_STATUS_IS_ADDITIONAL_CARD_PENDING_ACTIVATION";
    DeclineReason["CardStatusIsCancelled"] = "CARD_STATUS_IS_CANCELLED";
    DeclineReason["CardStatusIsClosed"] = "CARD_STATUS_IS_CLOSED";
    DeclineReason["CardStatusIsFrozen"] = "CARD_STATUS_IS_FROZEN";
    DeclineReason["CardStatusIsLost"] = "CARD_STATUS_IS_LOST";
    DeclineReason["CardStatusIsNewCardPendingActivation"] = "CARD_STATUS_IS_NEW_CARD_PENDING_ACTIVATION";
    DeclineReason["CardStatusIsPrefundedPendingActivation"] = "CARD_STATUS_IS_PREFUNDED_PENDING_ACTIVATION";
    DeclineReason["CardStatusIsPreloadedPendingActivation"] = "CARD_STATUS_IS_PRELOADED_PENDING_ACTIVATION";
    DeclineReason["CardStatusIsReissuedCardPendingActivation"] = "CARD_STATUS_IS_REISSUED_CARD_PENDING_ACTIVATION";
    DeclineReason["CardStatusIsReplacementCardPendingActivation"] = "CARD_STATUS_IS_REPLACEMENT_CARD_PENDING_ACTIVATION";
    DeclineReason["CardStatusIsStolen"] = "CARD_STATUS_IS_STOLEN";
    DeclineReason["CardStatusIsTransferred"] = "CARD_STATUS_IS_TRANSFERRED";
    DeclineReason["CashCreditLimitDeclined"] = "CASH_CREDIT_LIMIT_DECLINED";
    DeclineReason["DeniedAdviceMessageDecline"] = "DENIED_ADVICE_MESSAGE_DECLINE";
    DeclineReason["DuplicateTransmissionDetected"] = "DUPLICATE_TRANSMISSION_DETECTED";
    DeclineReason["ExceedsNumberOfPinTries"] = "EXCEEDS_NUMBER_OF_PIN_TRIES";
    DeclineReason["ExpiredCard"] = "EXPIRED_CARD";
    DeclineReason["FallbackCheckFailed"] = "FALLBACK_CHECK_FAILED";
    DeclineReason["FraudDeclined"] = "FRAUD_DECLINED";
    DeclineReason["HsmReturnedPinBlockError"] = "HSM_RETURNED_PIN_BLOCK_ERROR";
    DeclineReason["InsufficientFunds"] = "INSUFFICIENT_FUNDS";
    DeclineReason["InvalidCardNumber"] = "INVALID_CARD_NUMBER";
    DeclineReason["InvalidCardSequenceNumber"] = "INVALID_CARD_SEQUENCE_NUMBER";
    DeclineReason["InvalidCvc"] = "INVALID_CVC";
    DeclineReason["InvalidExpirationDate"] = "INVALID_EXPIRATION_DATE";
    DeclineReason["InvalidLoadRequest"] = "INVALID_LOAD_REQUEST";
    DeclineReason["InvalidPartnerStore"] = "INVALID_PARTNER_STORE";
    DeclineReason["InvalidPin"] = "INVALID_PIN";
    DeclineReason["InvalidTransactionAmount"] = "INVALID_TRANSACTION_AMOUNT";
    DeclineReason["InvalidTransactionCurrencyCode"] = "INVALID_TRANSACTION_CURRENCY_CODE";
    /** @deprecated */
    DeclineReason["MidIsInFraudList"] = "MID_IS_IN_FRAUD_LIST";
    DeclineReason["MerchantRestriction"] = "MERCHANT_RESTRICTION";
    DeclineReason["OfacVerificationFailed"] = "OFAC_VERIFICATION_FAILED";
    DeclineReason["ReferToCardIssuer"] = "REFER_TO_CARD_ISSUER";
    DeclineReason["RestrictedCardDecline"] = "RESTRICTED_CARD_DECLINE";
    DeclineReason["SecondaryCardLimitExceeded"] = "SECONDARY_CARD_LIMIT_EXCEEDED";
    DeclineReason["TrancodeMappingNotFound"] = "TRANCODE_MAPPING_NOT_FOUND";
    DeclineReason["Unspecified"] = "DECLINE_REASON_UNSPECIFIED";
    /** Debit-specific decline reasons */
    DeclineReason["InactiveAccount"] = "INACTIVE_ACCOUNT";
    DeclineReason["FundingSourceUnavailable"] = "FUNDING_SOURCE_UNAVAILABLE";
    DeclineReason["FundingSourceNotVerified"] = "FUNDING_SOURCE_NOT_VERIFIED";
    DeclineReason["UserTransactionLimitExceeded"] = "USER_TRANSACTION_LIMIT_EXCEEDED";
    DeclineReason["ProcessorUpstreamTimeout"] = "PROCESSOR_UPSTREAM_TIMEOUT";
    DeclineReason["OriginalTransactionNotFound"] = "ORIGINAL_TRANSACTION_NOT_FOUND";
    DeclineReason["AccountStateTransactionNotPermitted"] = "ACCOUNT_STATE_TRANSACTION_NOT_PERMITTED";
})(DeclineReason || (DeclineReason = {}));
/** Note: at time of writing, this is a FE-only enum value (i.e. doesn't exist in our BE protobuf definitions) */
var DisputeFlowVariant;
(function (DisputeFlowVariant) {
    DisputeFlowVariant["Unspecified"] = "DISPUTE_FLOW_VARIANT_UNSPECIFIED";
    /** non-Decisionly dispute workflow */
    DisputeFlowVariant["Manual"] = "MANUAL";
    /** Decisionly dispute workflow */
    DisputeFlowVariant["Automated"] = "AUTOMATED";
    /** Rent dispute workflow (Bilt) */
    DisputeFlowVariant["Rent"] = "RENT";
})(DisputeFlowVariant || (DisputeFlowVariant = {}));
var DisputeItemStatus;
(function (DisputeItemStatus) {
    DisputeItemStatus["DisputeItemStatusUnspecified"] = "DISPUTE_ITEM_STATUS_UNSPECIFIED";
    DisputeItemStatus["ReturnedToMerchant"] = "RETURNED_TO_MERCHANT";
    DisputeItemStatus["StillInMyPossession"] = "STILL_IN_MY_POSSESSION";
    DisputeItemStatus["CantReturnItem"] = "CANT_RETURN_ITEM";
})(DisputeItemStatus || (DisputeItemStatus = {}));
var DisputeRefundStatus;
(function (DisputeRefundStatus) {
    DisputeRefundStatus["DisputeRefundStatusUnspecified"] = "DISPUTE_REFUND_STATUS_UNSPECIFIED";
    DisputeRefundStatus["PartialRefund"] = "PARTIAL_REFUND";
    DisputeRefundStatus["NoRefund"] = "NO_REFUND";
    DisputeRefundStatus["UnableToContact"] = "UNABLE_TO_CONTACT";
})(DisputeRefundStatus || (DisputeRefundStatus = {}));
/** Canceled subscription dispute: still charged after cancel vs unaware of auto-renewal — matches backend protobuf */
var CancelledSubscriptionWhatHappened;
(function (CancelledSubscriptionWhatHappened) {
    CancelledSubscriptionWhatHappened["CancelledSubscriptionWhatHappenedUnspecified"] = "CANCELLED_SUBSCRIPTION_WHAT_HAPPENED_UNSPECIFIED";
    CancelledSubscriptionWhatHappened["CancelledSubscriptionStillCharged"] = "CANCELLED_SUBSCRIPTION_STILL_CHARGED";
    CancelledSubscriptionWhatHappened["CancelledSubscriptionUnawareOfAutoRenewal"] = "CANCELLED_SUBSCRIPTION_UNAWARE_OF_AUTO_RENEWAL";
})(CancelledSubscriptionWhatHappened || (CancelledSubscriptionWhatHappened = {}));
/** "What happened?" for charged multiple times (duplicate charge) — matches backend protobuf */
var DuplicateChargeWhatHappened;
(function (DuplicateChargeWhatHappened) {
    DuplicateChargeWhatHappened["DuplicateChargeWhatHappenedUnspecified"] = "DUPLICATE_CHARGE_WHAT_HAPPENED_UNSPECIFIED";
    DuplicateChargeWhatHappened["DuplicateChargeTwoCharges"] = "DUPLICATE_CHARGE_TWO_CHARGES";
    DuplicateChargeWhatHappened["DuplicateChargeWrongCardCharged"] = "DUPLICATE_CHARGE_WRONG_CARD_CHARGED";
    DuplicateChargeWhatHappened["DuplicateChargeAlreadyPaidWithCash"] = "DUPLICATE_CHARGE_ALREADY_PAID_WITH_CASH";
})(DuplicateChargeWhatHappened || (DuplicateChargeWhatHappened = {}));
/** Type tag for dispute supporting documents — matches backend protobuf */
var DisputeUploadType;
(function (DisputeUploadType) {
    DisputeUploadType["DisputeUploadTypeUnspecified"] = "DISPUTE_UPLOAD_TYPE_UNSPECIFIED";
    DisputeUploadType["ProofOfPurchase"] = "PROOF_OF_PURCHASE";
    DisputeUploadType["ProofOfReturn"] = "PROOF_OF_RETURN";
    DisputeUploadType["ProofOfCancellation"] = "PROOF_OF_CANCELLATION";
    DisputeUploadType["ProofOfContact"] = "PROOF_OF_CONTACT";
    DisputeUploadType["ProofOfRefundPromise"] = "PROOF_OF_REFUND_PROMISE";
})(DisputeUploadType || (DisputeUploadType = {}));
/** Card status at the time of dispute */
var DisputeCardStatus;
(function (DisputeCardStatus) {
    /** Status is not specified */
    DisputeCardStatus["CardStatusUnspecified"] = "CARD_STATUS_UNSPECIFIED";
    /** Card was reported lost */
    DisputeCardStatus["Lost"] = "LOST";
    /** Card was reported stolen */
    DisputeCardStatus["Stolen"] = "STOLEN";
    /** Card is in possession of cardholder */
    DisputeCardStatus["InPossession"] = "IN_POSSESSION";
})(DisputeCardStatus || (DisputeCardStatus = {}));
var DisputeReason;
(function (DisputeReason) {
    DisputeReason["Unspecified"] = "DISPUTE_REASON_UNSPECIFIED";
    DisputeReason["UnrecognizedCharge"] = "UNRECOGNIZED_CHARGE";
    DisputeReason["ChargedMultipleTimes"] = "CHARGED_MULTIPLE_TIMES";
    DisputeReason["Overcharged"] = "OVERCHARGED";
    DisputeReason["ItemNotReceived"] = "ITEM_NOT_RECEIVED";
    DisputeReason["ItemReceivedLate"] = "ITEM_RECEIVED_LATE";
    DisputeReason["ItemOrServiceNotAsDescribed"] = "ITEM_OR_SERVICE_NOT_AS_DESCRIBED";
    DisputeReason["ChargedForCancelledSubscription"] = "CHARGED_FOR_CANCELLED_SUBSCRIPTION";
    DisputeReason["NoRefundForReturnedItem"] = "NO_REFUND_FOR_RETURNED_ITEM";
})(DisputeReason || (DisputeReason = {}));
/**
 * Chargeback recovery posture — where the dispute sits from a
 * "did we recover funds from the merchant" lens, independent of the
 * cardholder-facing outcome.
 */
var DisputeRecoveryState;
(function (DisputeRecoveryState) {
    DisputeRecoveryState["Unspecified"] = "DISPUTE_RECOVERY_STATE_UNSPECIFIED";
    /** Chargeback not yet filed */
    DisputeRecoveryState["NotStarted"] = "NOT_STARTED";
    /** Active chargeback, pre-arb, or arbitration in flight */
    DisputeRecoveryState["PursuingRecovery"] = "PURSUING_RECOVERY";
    /** Provisional credit reversed; issuer made whole without pursuing recovery */
    DisputeRecoveryState["CreditReversed"] = "CREDIT_REVERSED";
    /** Funds recovered from merchant (chargeback won) */
    DisputeRecoveryState["Recovered"] = "RECOVERED";
    /** Cardless absorbs the loss (no recovery) */
    DisputeRecoveryState["Liable"] = "LIABLE";
})(DisputeRecoveryState || (DisputeRecoveryState = {}));
/** Lifecycle status of a Decisionly-routed dispute case */
var DecisionlyDisputeStatus;
(function (DecisionlyDisputeStatus) {
    DecisionlyDisputeStatus["Unspecified"] = "DECISIONLY_DISPUTE_STATUS_UNSPECIFIED";
    DecisionlyDisputeStatus["Created"] = "CREATED";
    DecisionlyDisputeStatus["ChargebackNeedsReview"] = "CHARGEBACK_NEEDS_REVIEW";
    DecisionlyDisputeStatus["ChargebackFiled"] = "CHARGEBACK_FILED";
    DecisionlyDisputeStatus["ChargebackRepresented"] = "CHARGEBACK_REPRESENTED";
    DecisionlyDisputeStatus["PrearbFiled"] = "PREARB_FILED";
    DecisionlyDisputeStatus["PrearbRebutted"] = "PREARB_REBUTTED";
    DecisionlyDisputeStatus["ArbitrationFiled"] = "ARBITRATION_FILED";
    DecisionlyDisputeStatus["TransactionPending"] = "TRANSACTION_PENDING";
    DecisionlyDisputeStatus["Accepted"] = "ACCEPTED";
    DecisionlyDisputeStatus["Rejected"] = "REJECTED";
    DecisionlyDisputeStatus["Withdrawn"] = "WITHDRAWN";
    DecisionlyDisputeStatus["Expired"] = "EXPIRED";
    DecisionlyDisputeStatus["MerchantCredited"] = "MERCHANT_CREDITED";
    DecisionlyDisputeStatus["Won"] = "WON";
    DecisionlyDisputeStatus["Lost"] = "LOST";
    DecisionlyDisputeStatus["Closed"] = "CLOSED";
    DecisionlyDisputeStatus["Queued"] = "QUEUED";
})(DecisionlyDisputeStatus || (DecisionlyDisputeStatus = {}));
/** Current state of the dispute lifecycle */
var DisputeStatus;
(function (DisputeStatus) {
    DisputeStatus["Unspecified"] = "DISPUTE_STATE_UNSPECIFIED";
    /** Cardholder just filed */
    DisputeStatus["Initiated"] = "INITIATED";
    /** Ticket opened */
    DisputeStatus["Received"] = "RECEIVED";
    /** Ops/processor working */
    DisputeStatus["UnderReview"] = "UNDER_REVIEW";
    /** Resolved in favor of merchant */
    DisputeStatus["Merchant"] = "MERCHANT";
    /** Resolved in favor of cardholder */
    DisputeStatus["Cardholder"] = "CARDHOLDER";
})(DisputeStatus || (DisputeStatus = {}));
/**
 * Dispute eligibility status
 */
var DisputeEligibility;
(function (DisputeEligibility) {
    /**
     * Default / unspecified status. User **should not** be able
     * to file a dispute against this transaction.
     */
    DisputeEligibility["Unspecified"] = "DISPUTE_ELIGIBILITY_UNSPECIFIED";
    /**
     * Transaction is eligible to be disputed. User **should** be
     * able to file a dispute against this transaction.
     */
    DisputeEligibility["Eligible"] = "ELIGIBLE";
    /**
     * Account is ineligible for disputes. User **should not** be
     * able to file a dispute against this transaction.
     */
    DisputeEligibility["AccountIneligible"] = "ACCOUNT_INELIGIBLE";
    /**
     * Card used for TXN was not issued by Cardless. User **should
     * not** be able to file a dispute against this transaction.
     * @example TXN made on Bilt account prior to conversion
     */
    DisputeEligibility["IssuerIneligible"] = "ISSUER_INELIGIBLE";
    /**
     * Transaction is ineligible for disputes. User **should not**
     * be able to file a dispute against this transaction.
     */
    DisputeEligibility["TransactionIneligible"] = "TRANSACTION_INELIGIBLE";
    /**
     * Transaction is ineligible for disputes because one already exists.
     * To protect against filing duplicate disputes, the user **should not**
     * be able to file another dispute against this transaction.
     */
    DisputeEligibility["DisputeInProgress"] = "DISPUTE_IN_PROGRESS";
})(DisputeEligibility || (DisputeEligibility = {}));
export { CancelledSubscriptionWhatHappened, DecisionlyDisputeStatus, DeclineReason, DigitalWalletType, DisputeCardStatus, DisputeEligibility, DisputeRecoveryState, DuplicateChargeWhatHappened, DisputeItemStatus, DisputeRefundStatus, DisputeReason, DisputeUploadType, DisputeStatus, DisputeFlowVariant, MerchantCategory, TransactionsEndpoints, TransactionStatus, TransactionType, TransactionSubType, FEE_SUB_TYPES, TransactionRewardsType, PaymentMethod, CARDLESS_MID, };
